import type { Event, EventHint } from '@sentry/vue'

/**
 * Keys that must never appear in Sentry event payloads.
 * Scrubbing is case-insensitive on key names.
 */
const DENIED_KEYS = new Set([
  // Auth / tokens
  'authorization',
  'cookie',
  'set-cookie',
  'x-xsrf-token',
  'xsrf-token',
  'csrf',
  '_token',
  'token',
  'bearer',
  'api_key',
  'apikey',
  'secret',
  'password',
  'password_confirmation',
  'remember_token',
  'startline_admin_token',
  'sanctum',
  // Stripe
  'stripe-signature',
  'client_secret',
  'payment_intent',
  'setup_intent',
  'charge',
  'customer',
  'account',
  // Mailgun
  'mailgun_signing_key',
  'signature',
  // Participant PII
  'email',
  'first_name',
  'last_name',
  'name',
  'full_name',
  'phone',
  'address',
  'street',
  'city',
  'postal_code',
  'zip',
  'date_of_birth',
  'dob',
  'national_register_number',
  'iban',
  // Organiser-internal free text (issue #222)
  'organizer_remarks',
])

/**
 * Regex patterns that match secret/PII values in any string field.
 * A match causes the entire string value to be replaced with [Filtered].
 */
const VALUE_PATTERNS: RegExp[] = [
  // Stripe object IDs
  /\b(pi|ch|cus|acct|seti|re|in|sub|prod|price)_[A-Za-z0-9]+\b/,
  // Stripe API keys
  /\b(sk|pk|rk)_(live|test)_[A-Za-z0-9]+\b/,
  // Stripe webhook secrets
  /\bwhsec_[A-Za-z0-9]+\b/,
  // Bearer tokens
  /Bearer\s+[A-Za-z0-9._-]+/i,
  // Email addresses
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/,
  // IBANs
  /\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/,
]

function isDeniedKey(key: string): boolean {
  return DENIED_KEYS.has(key.toLowerCase())
}

function containsSensitiveValue(value: string): boolean {
  return VALUE_PATTERNS.some((pattern) => pattern.test(value))
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (isDeniedKey(key)) {
      result[key] = '[Filtered]'
    } else if (typeof value === 'string' && containsSensitiveValue(value)) {
      result[key] = '[Filtered]'
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = scrubObject(value as Record<string, unknown>)
    } else {
      result[key] = value
    }
  }
  return result
}

/**
 * Sentry `beforeSend` hook for the Organizer SPA.
 *
 * Removes cookies, XSRF tokens, request headers, and any value matching the
 * PII / payment-data deny-list before the event is transmitted to Sentry.
 * `sendDefaultPii: false` in `Sentry.init` provides the first line of defence;
 * this scrubber is belt-and-braces.
 */
export function scrubEvent(event: Event, _hint: EventHint): Event | null {
  // Strip request cookies, headers, and query strings that could carry tokens.
  if (event.request) {
    event.request.cookies = undefined
    event.request.headers = undefined
    // Strip query string entirely — may carry token params.
    if (event.request.query_string) {
      event.request.query_string = undefined
    }
  }

  // Strip user identity down to nothing — email/name must never leave the app.
  if (event.user) {
    event.user = {}
  }

  // Scrub extra / contexts by key name and value pattern.
  if (event.extra) {
    event.extra = scrubObject(event.extra as Record<string, unknown>)
  }
  if (event.contexts) {
    event.contexts = scrubObject(event.contexts as Record<string, unknown>) as typeof event.contexts
  }

  // Scrub breadcrumb data.
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (breadcrumb.data) {
        return { ...breadcrumb, data: scrubObject(breadcrumb.data as Record<string, unknown>) }
      }
      return breadcrumb
    })
  }

  return event
}
